import React, {useContext, useEffect, useState} from "react"
import AuthContext from "../../context/AuthContext";
import AdminNav from "../../components/AdminNav/AdminNav";
import Loading from "../../components/Loading/Loading";
import Button from "../../components/Button/Button";
import useScheduledPostService from "../../api/services/useScheduledPostService";
import useFunction from "../../hooks/useFunction";
import swal from "sweetalert";

const PERSONAS = [
  {value: 'marktmeester', label: 'Marktmeester'},
  {value: 'evenementorganisator', label: 'Evenementorganisator'},
  {value: 'winkelcentrum_manager', label: 'Winkelcentrum Manager'},
  {value: 'gemeente', label: 'Gemeente'},
  {value: 'vastgoed', label: 'Vastgoed'},
]

const CONTENT_TYPES = [
  {value: 'educational', label: 'Educational'},
  {value: 'social_proof', label: 'Social Proof'},
  {value: 'insight', label: 'Insight'},
  {value: 'product', label: 'Product'},
  {value: 'engagement', label: 'Engagement'},
]

const STATUS_OPTIONS = [
  {value: 'all', label: 'Alle'},
  {value: 'pending', label: 'Gepland'},
  {value: 'posted', label: 'Geplaatst'},
  {value: 'failed', label: 'Mislukt'},
  {value: 'cancelled', label: 'Geannuleerd'},
]

const emptyForm = {
  content: '',
  content_twitter: '',
  comment_url: '',
  media_url: '',
  persona: 'marktmeester',
  content_type: 'educational',
  scheduled_at: '',
}

const UserAdminScheduledPosts = () => {

  const {user} = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState(true)
  const [posts, setPosts] = useState([])
  const [filterStatus, setFilterStatus] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({...emptyForm})
  const [formErrors, setFormErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const {adminIndex, adminStore, adminUpdate, adminDelete} = useScheduledPostService()
  const {truncate, dateTime} = useFunction()

  const loadPosts = () => {
    setIsLoading(true)
    adminIndex(
      (response) => {
        const sorted = (response.data || []).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
        setPosts(sorted)
        setIsLoading(false)
      },
      (errors) => {
        setIsLoading(false)
      },
      {status: filterStatus}
    )
  }

  useEffect(() => {
    loadPosts()
  }, [filterStatus]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}))
    if (formErrors[field]) {
      setFormErrors(prev => {
        const next = {...prev}
        delete next[field]
        return next
      })
    }
  }

  const handleNew = () => {
    setEditingId(null)
    setFormData({...emptyForm})
    setFormErrors({})
    setShowForm(true)
  }

  const handleEdit = (post) => {
    setEditingId(post.id)
    setFormData({
      content: post.content || '',
      content_twitter: post.content_twitter || '',
      comment_url: post.comment_url || '',
      media_url: post.media_url || '',
      persona: post.persona || 'marktmeester',
      content_type: post.content_type || 'educational',
      scheduled_at: post.scheduled_at ? post.scheduled_at.replace(' ', 'T').substring(0, 16) : '',
    })
    setFormErrors({})
    setShowForm(true)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingId(null)
    setFormData({...emptyForm})
    setFormErrors({})
  }

  const handleSubmit = () => {
    setIsSaving(true)
    setFormErrors({})

    const onSuccess = () => {
      setIsSaving(false)
      setShowForm(false)
      setEditingId(null)
      setFormData({...emptyForm})
      loadPosts()
      swal({
        title: 'Opgeslagen',
        icon: 'success',
        timer: 1000,
        buttons: false,
      })
    }

    const onError = (errors) => {
      setIsSaving(false)
      setFormErrors(errors || {})
    }

    if (editingId) {
      adminUpdate(onSuccess, onError, editingId, formData)
    } else {
      adminStore(onSuccess, onError, formData)
    }
  }

  const handleCancelPost = (post) => {
    swal({
      title: 'Post annuleren?',
      text: 'Weet je zeker dat je deze post wilt annuleren?',
      icon: 'warning',
      buttons: {
        cancel: {
          text: "Nee",
          value: null,
          visible: true,
          className: 'btn-color-grey btn-size-m',
          closeModal: true,
        },
        confirm: {
          text: "Ja, annuleren",
          value: true,
          visible: true,
          className: 'btn-color-red btn-size-m',
          closeModal: true,
        }
      }
    }).then((result) => {
      if (result) {
        adminDelete(
          () => {
            loadPosts()
            swal({
              title: 'Geannuleerd',
              icon: 'success',
              timer: 1000,
              buttons: false,
            })
          },
          () => {},
          post.id
        )
      }
    })
  }

  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending': return {backgroundColor: '#f0ad4e', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}
      case 'posted': return {backgroundColor: '#5cb85c', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}
      case 'failed': return {backgroundColor: '#d9534f', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}
      case 'cancelled': return {backgroundColor: '#999', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}
      default: return {backgroundColor: '#eee', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}
    }
  }

  return (
    <div className="page-user-admin">
      {user && user.is_admin &&
        <>
          <AdminNav/>
          <h2>Geplande Posts (LinkedIn + Twitter)</h2>

          <div className="form-row" style={{marginBottom: '20px', alignItems: 'flex-end', gap: '15px'}}>
            <div>
              <label><strong>Status:</strong></label><br/>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc'}}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Button
                label="Nieuw"
                color="green"
                size="s"
                onClick={handleNew}
              />
            </div>
          </div>

          {showForm &&
            <div style={{backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ddd'}}>
              <h3>{editingId ? 'Post bewerken' : 'Nieuwe post'}</h3>

              <div className="form-row" style={{gap: '15px', flexWrap: 'wrap'}}>
                <div>
                  <label><strong>Persona:</strong></label><br/>
                  <select
                    value={formData.persona}
                    onChange={(e) => handleFormChange('persona', e.target.value)}
                    style={{padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc'}}
                  >
                    {PERSONAS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label><strong>Content type:</strong></label><br/>
                  <select
                    value={formData.content_type}
                    onChange={(e) => handleFormChange('content_type', e.target.value)}
                    style={{padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc'}}
                  >
                    {CONTENT_TYPES.map(ct => (
                      <option key={ct.value} value={ct.value}>{ct.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label><strong>Gepland op:</strong></label><br/>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => handleFormChange('scheduled_at', e.target.value)}
                    style={{padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc'}}
                  />
                  {formErrors.scheduled_at && <p style={{color: 'red', fontSize: '12px'}}>{formErrors.scheduled_at}</p>}
                </div>
              </div>

              <div style={{marginTop: '15px'}}>
                <label><strong>Content (LinkedIn):</strong></label><br/>
                <textarea
                  value={formData.content}
                  onChange={(e) => handleFormChange('content', e.target.value)}
                  rows={5}
                  style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical'}}
                  placeholder="Hoofd content voor LinkedIn (max 3000 tekens)"
                />
                <small style={{color: '#999'}}>{formData.content.length}/3000</small>
                {formErrors.content && <p style={{color: 'red', fontSize: '12px'}}>{formErrors.content}</p>}
              </div>

              <div style={{marginTop: '15px'}}>
                <label><strong>Content (Twitter) - optioneel, kortere versie:</strong></label><br/>
                <textarea
                  value={formData.content_twitter}
                  onChange={(e) => handleFormChange('content_twitter', e.target.value)}
                  rows={3}
                  style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical'}}
                  placeholder="Korte versie voor Twitter (max 280 tekens). Leeg = zelfde als LinkedIn."
                />
                <small style={{color: formData.content_twitter.length > 280 ? 'red' : '#999'}}>{formData.content_twitter.length}/280</small>
                {formErrors.content_twitter && <p style={{color: 'red', fontSize: '12px'}}>{formErrors.content_twitter}</p>}
              </div>

              <div className="form-row" style={{gap: '15px', marginTop: '15px', flexWrap: 'wrap'}}>
                <div style={{flex: 1, minWidth: '200px'}}>
                  <label><strong>Comment URL (in 1e reactie):</strong></label><br/>
                  <input
                    type="text"
                    value={formData.comment_url}
                    onChange={(e) => handleFormChange('comment_url', e.target.value)}
                    style={{width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc'}}
                    placeholder="https://standplaats.nl/..."
                  />
                  {formErrors.comment_url && <p style={{color: 'red', fontSize: '12px'}}>{formErrors.comment_url}</p>}
                </div>
                <div style={{flex: 1, minWidth: '200px'}}>
                  <label><strong>Afbeelding URL:</strong></label><br/>
                  <input
                    type="text"
                    value={formData.media_url}
                    onChange={(e) => handleFormChange('media_url', e.target.value)}
                    style={{width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc'}}
                    placeholder="https://..."
                  />
                  {formErrors.media_url && <p style={{color: 'red', fontSize: '12px'}}>{formErrors.media_url}</p>}
                  {formData.media_url &&
                    <img
                      src={formData.media_url}
                      alt="Preview"
                      style={{marginTop: '8px', maxWidth: '200px', maxHeight: '200px', borderRadius: '4px', border: '1px solid #ccc', objectFit: 'cover'}}
                      onError={(e) => e.target.style.display = 'none'}
                      onLoad={(e) => e.target.style.display = 'block'}
                    />
                  }
                </div>
              </div>

              <div className="form-row" style={{gap: '10px', marginTop: '20px'}}>
                <Button
                  label={isSaving ? "Opslaan..." : "Opslaan"}
                  color="green"
                  size="s"
                  onClick={handleSubmit}
                  disabled={isSaving}
                />
                <Button
                  label="Annuleren"
                  color="grey"
                  size="s"
                  onClick={handleCancel}
                />
              </div>
            </div>
          }

          {isLoading
            ? <Loading/>
            : <>
              {posts.length === 0
                ? <p>Geen geplande posts gevonden.</p>
                : <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead>
                      <tr style={{borderBottom: '2px solid #ddd', textAlign: 'left'}}>
                        <th style={{padding: '8px'}}>Content</th>
                        <th style={{padding: '8px'}}>Persona</th>
                        <th style={{padding: '8px'}}>Type</th>
                        <th style={{padding: '8px'}}>Gepland op</th>
                        <th style={{padding: '8px'}}>Status</th>
                        <th style={{padding: '8px'}}>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((post) => (
                        <tr key={post.id} style={{borderBottom: '1px solid #eee'}}>
                          <td style={{padding: '8px', maxWidth: '400px'}}>
                            {post.content ? truncate(post.content, 120) : '-'}
                            {post.content_twitter && <><br/><small style={{color: '#999'}}>Twitter: {truncate(post.content_twitter, 60)}</small></>}
                          </td>
                          <td style={{padding: '8px'}}>
                            {post.persona || '-'}
                          </td>
                          <td style={{padding: '8px'}}>
                            {post.content_type || '-'}
                          </td>
                          <td style={{padding: '8px', whiteSpace: 'nowrap'}}>
                            {post.scheduled_at ? dateTime(post.scheduled_at) : '-'}
                          </td>
                          <td style={{padding: '8px'}}>
                            <span style={getStatusStyle(post.status)}>
                              {STATUS_OPTIONS.find(s => s.value === post.status)?.label || post.status}
                            </span>
                          </td>
                          <td style={{padding: '8px', whiteSpace: 'nowrap'}}>
                            <div className="form-row" style={{gap: '5px'}}>
                              {post.status === 'pending' &&
                                <>
                                  <Button
                                    label="Edit"
                                    color="blue"
                                    size="xs"
                                    onClick={() => handleEdit(post)}
                                  />
                                  <Button
                                    label="Annuleer"
                                    color="red"
                                    size="xs"
                                    onClick={() => handleCancelPost(post)}
                                  />
                                </>
                              }
                              {post.external_url &&
                                <a href={post.external_url} target="_blank" rel="noopener noreferrer" style={{fontSize: '12px'}}>Bekijk</a>
                              }
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              }
            </>
          }
        </>
      }
    </div>
  )
}

export default UserAdminScheduledPosts
